import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-toastify';
import { Task } from '@models/task.model';
import { createSubtaskAPI, CreateSubtaskParams } from '@services/task';
import { useTranslation } from '@hooks/useTranslation';

interface UseCreateSubtaskReturn {
  createSubtask: (params: CreateSubtaskParams) => Promise<boolean>;
  createSubtaskWithData: (params: CreateSubtaskParams) => Promise<Task | null>;
  loading: boolean;
}

export const useCreateSubtask = (): UseCreateSubtaskReturn => {
  const tActionMessages = useTranslation('actionMessages');
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: createSubtaskAPI,
    onSuccess: (data) => {
      if (data?.createSubtask?.taskId) {
        toast.success(tActionMessages.subtaskCreatedSuccessfully);

        // Invalidate tasks queries to refresh the task list
        queryClient.invalidateQueries({ queryKey: ['tasks'] });
        queryClient.invalidateQueries({ queryKey: ['task', data.createSubtask.taskId] });
      }
    },
    onError: (error: any) => {
      const errorMessage =
        error?.response?.errors[0]?.message || 'Error creating subtask';
      toast.error(errorMessage);
    },
  });

  const createSubtask = async (params: CreateSubtaskParams): Promise<boolean> => {
    try {
      const result = await mutation.mutateAsync(params);
      return !!result?.createSubtask?.taskId;
    } catch (error) {
      return false;
    }
  };

  const createSubtaskWithData = async (params: CreateSubtaskParams): Promise<Task | null> => {
    try {
      const result = await mutation.mutateAsync(params);
      return result?.createSubtask || null;
    } catch (error) {
      return null;
    }
  };

  return {
    createSubtask,
    createSubtaskWithData,
    loading: mutation.isPending
  };
};